import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  EyeIcon,
  PencilIcon,
  TrashIcon,
  DocumentTextIcon,
  CalendarIcon,
  UserIcon,
  ListBulletIcon,
  ArrowDownTrayIcon
} from "@heroicons/react/24/outline";
import { toast } from 'react-toastify';
import {
  fetchTreatmentPlans,
  deleteTreatmentPlan,
  setCurrentTreatmentPlan
} from "../../store/thunks/treatmentPlanThunk";
import TreatmentPlanForm from "./TreatmentPlanForm";
import ConfirmDialog from "./ConfirmDialog";
import SearchDropdown from "./SearchDropdown";
import { downloadTreatmentPlanPDF } from "../../utils/treatmentPlanPdfGenerator";

const TreatmentPlanList = ({ onClose }) => {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);
  const {
    treatmentPlans,
    pagination,
    loading,
    deleteLoading,
    error
  } = useSelector((state) => state.treatmentPlan);

  const [selectedPlan, setSelectedPlan] = useState(null);
  const [editingPlan, setEditingPlan] = useState(null);
  const [showEditForm, setShowEditForm] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedClient, setSelectedClient] = useState(null);

  // Load treatment plans on component mount
  useEffect(() => {
    console.log(`[Treatment Plan List] Loading treatment plans`);
    const params = { page: currentPage, limit: 10 };
    if (selectedClient?._id) {
      params.clientId = selectedClient._id;
    }
    dispatch(fetchTreatmentPlans(params));
  }, [dispatch, currentPage, selectedClient]);

  const handleView = (plan) => {
    console.log(`[Treatment Plan List] Viewing plan:`, plan._id);
    setSelectedPlan(plan);
    setShowViewModal(true);
  };

  const handleEdit = (plan) => {
    console.log(`[Treatment Plan List] Editing plan:`, plan._id);
    setEditingPlan(plan);
    setShowEditForm(true);
  };

  const handleDelete = (plan) => {
    console.log(`[Treatment Plan List] Confirming delete for plan:`, plan._id);
    setConfirmDelete(plan);
  };

  const handleDownloadPDF = async (plan) => {
    try {
      console.log(`[Treatment Plan List] Downloading PDF for:`, plan.clientName);
      await downloadTreatmentPlanPDF(plan);
      toast.success("PDF downloaded successfully");
    } catch (error) {
      console.error(`[Treatment Plan List] Error downloading PDF:`, error);
      toast.error("Failed to download PDF");
    }
  };

  const confirmDeletePlan = async () => {
    if (!confirmDelete) return;

    try {
      console.log(`[Treatment Plan List] Deleting plan:`, confirmDelete._id);
      await dispatch(deleteTreatmentPlan(confirmDelete._id)).unwrap();
      toast.success("Treatment plan deleted successfully");
      setConfirmDelete(null);

      // Refresh the list
      dispatch(fetchTreatmentPlans({ page: currentPage, limit: 10 }));
    } catch (error) {
      console.error(`[Treatment Plan List] Error deleting plan:`, error);
      toast.error("Failed to delete treatment plan");
    }
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleClientSelection = (client) => {
    console.log(`[Treatment Plan List] Client selected for filtering:`, client);
    setSelectedClient(client);
    setCurrentPage(1); // Reset to first page when filtering
  };

  const handleClearFilter = () => {
    console.log(`[Treatment Plan List] Clearing client filter`);
    setSelectedClient(null);
    setCurrentPage(1);
  };

  const handleEditFormSave = () => {
    // Refresh the list after saving
    dispatch(fetchTreatmentPlans({ page: currentPage, limit: 10 }));
    setShowEditForm(false);
    setEditingPlan(null);
  };

  if (loading) {
    return (
      <div className="fixed inset-0 flex items-center justify-center z-70">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={onClose} />
        <div className="relative bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 p-6">
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
            <span className="ml-2 text-gray-600">Loading treatment plans...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="fixed inset-0 flex items-center justify-center z-70">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={onClose} />
        <div className="relative bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 p-6">
          <div className="text-center">
            <div className="text-red-600 mb-4">Error: {error}</div>
            <button
              onClick={() => dispatch(fetchTreatmentPlans({ page: currentPage, limit: 10 }))}
              className="bg-indigo-600 text-white px-4 py-2 rounded hover:bg-indigo-700">
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="fixed inset-0 flex items-center justify-center z-70 overflow-y-auto">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={onClose} />
        <div className="relative bg-white rounded-lg shadow-xl max-w-7xl w-full mx-4 max-h-[95vh] overflow-y-auto">
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-900">
                Treatment Plans
              </h2>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-500">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Search and Filter */}
            <div className="flex items-center space-x-3">
              <div className="flex-1">
                <SearchDropdown
                  displayFields={["firstName", "lastName"]}
                  onSelection={handleClientSelection}
                  prefix=":client:"
                  placeholder="Search by client name to filter..."
                  initialSelection={selectedClient}
                  separator=" "
                />
              </div>
              {selectedClient && (
                <button
                  onClick={handleClearFilter}
                  className="px-4 py-2 text-sm bg-gray-100 text-gray-700 rounded-sm hover:bg-gray-200 transition-colors">
                  Clear Filter
                </button>
              )}
            </div>

            {/* Active Filter Display */}
            {selectedClient && (
              <div className="mt-3 flex items-center text-sm text-gray-600">
                <span className="font-medium">Filtered by:</span>
                <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                  {selectedClient.firstName} {selectedClient.lastName}
                </span>
              </div>
            )}
          </div>

          {/* Content */}
          <div className="p-6">
            {treatmentPlans.length === 0 ? (
              <div className="text-center py-12">
                <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No treatment plans</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Get started by creating your first treatment plan.
                </p>
              </div>
            ) : (
              <>
                {/* Treatment Plans Grid */}
                <div className="grid grid-cols-1 gap-4">
                  {treatmentPlans.map((plan) => {
                    return (
                      <div
                        key={plan._id}
                        className="bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-shadow p-6">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            {/* Header Info */}
                            <div className="flex items-center space-x-4 mb-3">
                              <div className="flex items-center">
                                <UserIcon className="h-5 w-5 text-gray-400 mr-2" />
                                <span className="font-medium text-gray-900">
                                  {plan.clientName}
                                </span>
                                {plan.clientId && (
                                  <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                    Linked Client
                                  </span>
                                )}
                              </div>

                              <div className="flex items-center">
                                <CalendarIcon className="h-5 w-5 text-gray-400 mr-2" />
                                <span className="text-sm text-gray-600">
                                  {new Date(plan.date).toLocaleDateString()}
                                </span>
                              </div>

                              <div className="flex items-center">
                                <ListBulletIcon className="h-5 w-5 text-gray-400 mr-2" />
                                <span className="text-sm text-gray-600">
                                  {plan.goals?.length || 0} goal(s)
                                </span>
                              </div>
                            </div>

                            {/* Plan Details */}
                            <div className="grid grid-cols-2 gap-4 mb-4">
                              <div>
                                <div className="text-sm text-gray-500">Chronological Age</div>
                                <div className="text-sm font-medium">{plan.chronologicalAge || 'N/A'}</div>
                              </div>
                              <div>
                                <div className="text-sm text-gray-500">Grade</div>
                                <div className="text-sm font-medium">{plan.grade || 'N/A'}</div>
                              </div>
                              <div>
                                <div className="text-sm text-gray-500">School</div>
                                <div className="text-sm font-medium">{plan.school || 'N/A'}</div>
                              </div>
                              <div>
                                <div className="text-sm text-gray-500">Clinician(s)</div>
                                <div className="text-sm font-medium">{plan.clinicians || 'N/A'}</div>
                              </div>
                            </div>

                            {/* Communication Status Preview */}
                            {plan.currentCommunicationStatus && (
                              <div className="mb-4">
                                <div className="text-sm text-gray-500 mb-1">Communication Status</div>
                                <div
                                  className="text-sm text-gray-700 line-clamp-2 prose prose-sm max-w-none [&>p]:mb-1 [&_strong]:font-semibold [&_em]:italic [&_u]:underline"
                                  dangerouslySetInnerHTML={{
                                    __html: plan.currentCommunicationStatus.substring(0, 150)
                                  }}
                                />
                              </div>
                            )}

                            {/* Treatment Plan Preview */}
                            {plan.treatmentPlan && (
                              <div className="mb-4">
                                <div className="text-sm text-gray-500 mb-1">Treatment Plan</div>
                                <div
                                  className="text-sm text-gray-700 line-clamp-2 prose prose-sm max-w-none [&>p]:mb-1 [&_strong]:font-semibold [&_em]:italic [&_u]:underline"
                                  dangerouslySetInnerHTML={{
                                    __html: plan.treatmentPlan.substring(0, 150)
                                  }}
                                />
                              </div>
                            )}

                            {/* Created By */}
                            {plan.createdBy && (
                              <div className="text-xs text-gray-500">
                                Created by: {plan.createdBy.firstName} {plan.createdBy.lastName}
                              </div>
                            )}
                          </div>

                          {/* Action Buttons */}
                          <div className="flex items-center space-x-2 ml-4">
                            <button
                              onClick={() => handleView(plan)}
                              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full"
                              title="View Details">
                              <EyeIcon className="h-5 w-5" />
                            </button>

                            <button
                              onClick={() => handleDownloadPDF(plan)}
                              className="p-2 text-indigo-400 hover:text-indigo-600 hover:bg-indigo-100 rounded-full"
                              title="Download PDF">
                              <ArrowDownTrayIcon className="h-5 w-5" />
                            </button>

                            {/* Only allow editing if user created it or is admin */}
                            {(plan.createdBy?._id === user._id || user.role?.includes('admin')) && (
                              <button
                                onClick={() => handleEdit(plan)}
                                className="p-2 text-blue-400 hover:text-blue-600 hover:bg-blue-100 rounded-full"
                                title="Edit">
                                <PencilIcon className="h-5 w-5" />
                              </button>
                            )}

                            {/* Only allow deletion if user created it or is admin */}
                            {(plan.createdBy?._id === user._id || user.role?.includes('admin')) && (
                              <button
                                onClick={() => handleDelete(plan)}
                                className="p-2 text-red-400 hover:text-red-600 hover:bg-red-100 rounded-full"
                                title="Delete">
                                <TrashIcon className="h-5 w-5" />
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Pagination */}
                {pagination.totalPages > 1 && (
                  <div className="mt-6 flex items-center justify-between">
                    <div className="text-sm text-gray-700">
                      Showing {((pagination.page - 1) * pagination.limit) + 1} to{' '}
                      {Math.min(pagination.page * pagination.limit, pagination.totalDocs)} of{' '}
                      {pagination.totalDocs} results
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handlePageChange(pagination.page - 1)}
                        disabled={!pagination.hasPrevPage}
                        className="px-3 py-2 text-sm bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed">
                        Previous
                      </button>
                      <span className="px-3 py-2 text-sm bg-indigo-50 border border-indigo-200 rounded-md">
                        Page {pagination.page} of {pagination.totalPages}
                      </span>
                      <button
                        onClick={() => handlePageChange(pagination.page + 1)}
                        disabled={!pagination.hasNextPage}
                        className="px-3 py-2 text-sm bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed">
                        Next
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* View Modal */}
      {showViewModal && selectedPlan && (
        <TreatmentPlanViewModal
          plan={selectedPlan}
          onClose={() => {
            setShowViewModal(false);
            setSelectedPlan(null);
          }}
        />
      )}

      {/* Edit Form Modal */}
      {showEditForm && editingPlan && (
        <TreatmentPlanForm
          editingTreatmentPlan={editingPlan}
          onClose={() => {
            setShowEditForm(false);
            setEditingPlan(null);
          }}
          onSave={handleEditFormSave}
        />
      )}

      {/* Confirm Delete Dialog */}
      {confirmDelete && (
        <ConfirmDialog
          isOpen={true}
          title="Delete Treatment Plan"
          message={`Are you sure you want to delete the treatment plan for ${confirmDelete.clientName}? This action cannot be undone.`}
          confirmText="Delete"
          type="danger"
          onConfirm={confirmDeletePlan}
          onClose={() => setConfirmDelete(null)}
        />
      )}
    </>
  );
};

// View Modal Component
const TreatmentPlanViewModal = ({ plan, onClose }) => {
  const handleDownloadPDF = async () => {
    try {
      console.log(`[Treatment Plan View Modal] Downloading PDF for:`, plan.clientName);
      await downloadTreatmentPlanPDF(plan);
      toast.success("PDF downloaded successfully");
    } catch (error) {
      console.error(`[Treatment Plan View Modal] Error downloading PDF:`, error);
      toast.error("Failed to download PDF");
    }
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center z-[100] overflow-y-auto">
      <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={onClose} />
      <div className="relative bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[95vh] overflow-y-auto">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900">
              Treatment Plan - {plan.clientName}
            </h2>
            <div className="flex items-center space-x-2">
              <button
                onClick={handleDownloadPDF}
                className="inline-flex items-center px-3 py-2 bg-indigo-600 text-white text-sm font-medium rounded-sm hover:bg-indigo-700 transition-colors"
                title="Download PDF">
                <ArrowDownTrayIcon className="h-4 w-4 mr-1" />
                Download PDF
              </button>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-500">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Basic Info */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Date</label>
              <div className="mt-1 text-sm text-gray-900">
                {new Date(plan.date).toLocaleDateString()}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Date of Birth</label>
              <div className="mt-1 text-sm text-gray-900">
                {plan.dateOfBirth ? new Date(plan.dateOfBirth).toLocaleDateString() : 'N/A'}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Chronological Age</label>
              <div className="mt-1 text-sm text-gray-900">{plan.chronologicalAge || 'N/A'}</div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Grade</label>
              <div className="mt-1 text-sm text-gray-900">{plan.grade || 'N/A'}</div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">School</label>
              <div className="mt-1 text-sm text-gray-900">{plan.school || 'N/A'}</div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Clinician(s)</label>
              <div className="mt-1 text-sm text-gray-900">{plan.clinicians || 'N/A'}</div>
            </div>
          </div>

          {/* Speech Language Pathologist */}
          {plan.speechLanguagePathologist && (
            <div>
              <label className="block text-sm font-medium text-gray-700">Speech Language Pathologist</label>
              <div className="mt-1 text-sm text-gray-900">{plan.speechLanguagePathologist}</div>
            </div>
          )}

          {/* Communication Status */}
          {plan.currentCommunicationStatus && (
            <div>
              <label className="block text-sm font-medium text-gray-700">Current Communication Status</label>
              <div
                className="mt-1 p-3 bg-gray-50 rounded-md text-sm text-gray-900 prose prose-sm max-w-none [&>p]:mb-2 [&>ul]:mb-2 [&>ol]:mb-2 [&_strong]:font-semibold [&_em]:italic [&_u]:underline [&_ul]:list-disc [&_ol]:list-decimal"
                dangerouslySetInnerHTML={{ __html: plan.currentCommunicationStatus }}
              />
            </div>
          )}

          {/* Treatment Plan */}
          {plan.treatmentPlan && (
            <div>
              <label className="block text-sm font-medium text-gray-700">Treatment Plan</label>
              <div
                className="mt-1 p-3 bg-gray-50 rounded-md text-sm text-gray-900 prose prose-sm max-w-none [&>p]:mb-2 [&>ul]:mb-2 [&>ol]:mb-2 [&_strong]:font-semibold [&_em]:italic [&_u]:underline [&_ul]:list-disc [&_ol]:list-decimal"
                dangerouslySetInnerHTML={{ __html: plan.treatmentPlan }}
              />
            </div>
          )}

          {/* Goals */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">Goals</label>
            <div className="space-y-3">
              {plan.goals?.map((goal, index) => (
                <div key={goal.id || index} className="p-4 border border-gray-200 rounded-md bg-gray-50">
                  <div className="flex items-start gap-2">
                    <span className="font-semibold text-gray-700 flex-shrink-0">{index + 1}.</span>
                    <div
                      className="text-sm text-gray-900 flex-1 prose prose-sm max-w-none [&>p]:mb-2 [&>ul]:mb-2 [&>ol]:mb-2 [&_strong]:font-semibold [&_em]:italic [&_u]:underline [&_ul]:list-disc [&_ol]:list-decimal"
                      dangerouslySetInnerHTML={{ __html: goal.description }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200">
          <button
            onClick={onClose}
            className="w-full bg-gray-100 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-200">
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default TreatmentPlanList;
